// 404 Not Found Page - Mesh Network Themed
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export function NotFoundPage() {
  const [signalStrength, setSignalStrength] = useState(0);
  const [attemptCount, setAttemptCount] = useState(0);
  const [isPulsing, setIsPulsing] = useState(false);

  // Simulate searching for signal (slower animation)
  useEffect(() => {
    const interval = setInterval(() => {
      setSignalStrength(Math.random());
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  // Handle retry with pulse animation
  const handleRetry = () => {
    setAttemptCount(attemptCount + 1);
    setIsPulsing(true);
    
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    
    // Reset pulse animation after completion
    setTimeout(() => setIsPulsing(false), 1000);
  };

  const messages = [
    "📡 Signal Lost",
    "🔍 Node Not Found",
    "📦 Packet Dropped",
    "🌐 Out of Range",
    "🗺️ Off the Mesh",
  ];

  const wittyMessages = [
    "Looks like this page took a detour through the mesh and never made it back.",
    "This page couldn't be reached. Maybe it's behind a mountain? 🏔️",
    "404 hops later... still can't find this page.",
    "The mesh is strong, but this route doesn't exist...",
    "Even with perfect LoRa range, we can't find this page...",
    "This page is lost in the mesh... try again later.",
  ];

  const randomMessage = messages[Math.floor(Math.random() * messages.length)];
  const randomWitty = wittyMessages[Math.floor(Math.random() * wittyMessages.length)];

  return (
    <div className="relative z-10 min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-16">
      <div className="max-w-2xl w-full">
        {/* Main Error Card */}
        <div className="bg-zinc-900/80 backdrop-blur-md border border-zinc-800/60 rounded-2xl p-8 md:p-12 text-center pointer-events-auto">
          {/* Animated 404 with mesh network styling */}
          <div className="mb-6">
            <h1 className="text-8xl md:text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-cyan-500 to-cyan-600 mb-4 select-none">
              404
            </h1>
            <div className="flex items-center justify-center gap-2 text-xl md:text-2xl font-semibold text-cyan-400 mb-2">
              {randomMessage}
            </div>
          </div>

          {/* Signal Strength Animation - Bidirectional */}
          <div className="mb-8">
            <div className="relative inline-block">
              {/* Pulse animation rings */}
              {isPulsing && (
                <>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full border-2 border-cyan-400 animate-ping opacity-75" />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center" style={{ animationDelay: '200ms' }}>
                    <div className="w-20 h-20 rounded-full border-2 border-cyan-400 animate-ping opacity-50" />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center" style={{ animationDelay: '400ms' }}>
                    <div className="w-24 h-24 rounded-full border-2 border-cyan-400 animate-ping opacity-25" />
                  </div>
                </>
              )}
              
              {/* Signal bars - mirrored to show bidirectional mesh signal */}
              <div className="flex items-center justify-center gap-1 mb-2 relative">
                {/* Left side (outgoing) */}
                <div 
                  className={`w-1.5 h-10 rounded-full transition-all duration-300 ${signalStrength >= 0.75 ? 'bg-cyan-400' : 'bg-zinc-700'}`}
                />
                <div 
                  className={`w-1.5 h-8 rounded-full transition-all duration-300 ${signalStrength >= 0.5 ? 'bg-cyan-400' : 'bg-zinc-700'}`}
                />
                <div 
                  className={`w-1.5 h-6 rounded-full transition-all duration-300 ${signalStrength >= 0.25 ? 'bg-cyan-400' : 'bg-zinc-700'}`}
                />
                <div 
                  className={`w-1.5 h-4 rounded-full transition-all duration-300 ${signalStrength >= 0 ? 'bg-cyan-400' : 'bg-zinc-700'}`}
                />
                
                {/* Center node/dot */}
                <div className="w-2 h-2 rounded-full bg-cyan-400 mx-1" />
                
                {/* Right side (incoming) - mirrored */}
                <div 
                  className={`w-1.5 h-4 rounded-full transition-all duration-300 ${signalStrength >= 0 ? 'bg-cyan-400' : 'bg-zinc-700'}`}
                />
                <div 
                  className={`w-1.5 h-6 rounded-full transition-all duration-300 ${signalStrength >= 0.25 ? 'bg-cyan-400' : 'bg-zinc-700'}`}
                />
                <div 
                  className={`w-1.5 h-8 rounded-full transition-all duration-300 ${signalStrength >= 0.5 ? 'bg-cyan-400' : 'bg-zinc-700'}`}
                />
                <div 
                  className={`w-1.5 h-10 rounded-full transition-all duration-300 ${signalStrength >= 0.75 ? 'bg-cyan-400' : 'bg-zinc-700'}`}
                />
              </div>
            </div>
            <p className="text-sm text-zinc-500">Searching for route...</p>
          </div>

          {/* Witty Message */}
          <p className="text-zinc-300 text-base md:text-lg mb-8 leading-relaxed">
            {randomWitty}
          </p>

          {/* Retry Counter */}
          <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-lg p-4 mb-8">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400">Retry Attempts:</span>
              <span className="text-cyan-400 font-mono font-medium">{attemptCount}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-zinc-400">Hops to Destination:</span>
              <span className="text-red-400 font-mono font-medium">∞</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-cyan-400 text-zinc-900 font-semibold rounded-xl hover:bg-cyan-300 transition-all hover:scale-105"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Return to Base Node
            </Link>
            <button
              onClick={handleRetry}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-zinc-800 text-cyan-400 font-semibold rounded-xl hover:bg-zinc-700 border border-cyan-400/30 transition-all hover:scale-105"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Retry Connection ({attemptCount})
            </button>
          </div>

          {/* Additional Help Links */}
          <div className="mt-8 pt-6 border-t border-zinc-800">
            <p className="text-sm text-zinc-500 mb-3">Need help finding your way?</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/community-resources" className="text-cyan-400 hover:text-cyan-300 transition">
                Community Resources
              </Link>
              <Link to="/mesh-tools" className="text-cyan-400 hover:text-cyan-300 transition">
                Mesh Tools
              </Link>
              <a href="https://meshtastic.org/" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 transition">
                Meshtastic Docs
              </a>
            </div>
          </div>
        </div>

        {/* Fun Technical Details */}
        <div className="mt-6 text-center">
          <details className="text-xs text-zinc-600 cursor-pointer hover:text-zinc-500 transition">
            <summary className="inline-flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Technical Details
            </summary>
            <div className="mt-2 p-3 bg-zinc-950/50 rounded-lg font-mono text-left">
              <div>ERROR_CODE: MESH_NODE_NOT_FOUND</div>
              <div>STATUS: 404 • Packet Lost in Transit</div>
              <div>PROTOCOL: HTTP/1.1 (not LoRa, sadly)</div>
              <div>SUGGESTED_ACTION: Reroute to home node</div>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}

